import React, { useEffect, useRef, useState } from "react";
import { ApiResponse } from "@/types/Api.tsx";
import { useQuery } from "@tanstack/react-query";
import { User } from "@/types/User.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import { UserClientsQueryKey } from "@/config/constants/QueryKeys.tsx";
import { useAppSelector } from "@/store";
import { getUser } from "@/utils/localStorage";
import { checkPermissionByKeyAndValue, isBitramp } from "@/utils/utils";
import CustomPOSTableShort from "@/components/POSTableShort";

const POSClients: React.FC = () => {
  const {
    data: clients,
    isLoading: isLoadingUsers,
    error: errorUsers,
  } = useQuery<ApiResponse<User[]>>({
    queryFn: async () => GenericApi.get<User[]>(ApiRoutes.CLIENT),
    queryKey: [UserClientsQueryKey],
  });

  const user = getUser();

  const canEditTransactions =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "transaction"
    ) || user?.is_superuser;

  const clientTab = useAppSelector(
    (state) => state.clientTransfer.clientTransfer
  );

  const canEditFee =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "exchangefee"
    ) || user?.is_superuser;

  const inboxUser = useAppSelector((state) => state.inboxSettings.inboxUser);
  const [expandedRow, setExpandedRow] = useState<number | null>(
    clientTab ? clientTab : inboxUser ? inboxUser : null
  );
  const [searchTerm, setSearchTerm] = useState("");

  const [sortField, setSortField] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const rowRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // useEffect(() => {
  //   if (expandedRow !== null && rowRefs.current[expandedRow]) {
  //     rowRefs.current[expandedRow]?.scrollIntoView({
  //       behavior: "smooth",
  //       block: "center",
  //     });
  //   }
  // }, [expandedRow]);

  const toggleRow = (index: number, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest(".custom-table-short")) {
      event.stopPropagation();
      return;
    }

    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredClients = clients?.data?.filter(
    (client) =>
      client.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortData = (clients: User[]) => {
    return clients.sort((a, b) => {
      let comparison = 0;
      if (sortField === "name") {
        comparison = a.first_name.localeCompare(b.first_name);
      } else if (sortField === "joinDate") {
        comparison =
          new Date(a.date_joined).getTime() - new Date(b.date_joined).getTime();
      } else if (sortField === "transactions") {
        comparison = a.transaction_count - b.transaction_count;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  };

  const tableRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
      setExpandedRow(null);
    }
  };

  const formatDate = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (isLoadingUsers) {
    return <div>Loading...</div>;
  }

  if (errorUsers) {
    return <div>Error: {errorUsers?.message}</div>;
  }

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex justify-between items-center px-8 pt-8 pb-3">
        <h2 className="Heading2 bold dark-blue">Clients</h2>
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          className="p-2 border-[#ffffff] placeholder-[#73779130] rounded-lg text-black font-satoshi text-[1rem] font-medium w-[30%] pshadow Input pshadow"
        />
        <div className="flex items-center gap-4">
          <button
            className={`Primary_button`}
            onClick={() => handleSort("name")}
          >
            {sortField == "name" && sortDirection == "asc" ? (
              <span>A..Z</span>
            ) : sortField == "name" && sortDirection == "desc" ? (
              <span>Z..A</span>
            ) : (
              ""
            )}{" "}
            Name
          </button>
          <button
            className={`Primary_button`}
            onClick={() => handleSort("joinDate")}
          >
            {sortField == "joinDate" && sortDirection == "asc" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
              >
                <path
                  d="M4.66406 3.93475L1.96227 6.13974C1.57552 6.38804 1.16012 6.43753 0.73059 6.25275C-0.104787 5.89268 -0.238963 4.79307 0.40616 4.18223C1.09407 3.53096 1.99134 2.89784 2.73284 2.28535C3.2172 1.88527 3.70032 1.4856 4.18676 1.08923C4.94861 0.468485 5.6764 -0.481812 6.73859 0.287416L11.5818 4.30514C12.4973 5.45671 11.1435 6.92423 9.89981 6.14015L7.19802 3.93517L7.19802 11.3387C7.19802 11.4204 7.08711 11.7112 7.03975 11.8003C6.91181 12.0416 6.63972 12.2894 6.37843 12.381C5.81473 12.5782 5.09442 12.355 4.82149 11.8011C4.78494 11.7273 4.66406 11.3577 4.66406 11.2975L4.66406 3.93517L4.66406 3.93475Z"
                  fill="white"
                />
              </svg>
            ) : sortField == "joinDate" && sortDirection == "desc" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                className="rotate-180"
              >
                <path
                  d="M4.66406 3.93475L1.96227 6.13974C1.57552 6.38804 1.16012 6.43753 0.73059 6.25275C-0.104787 5.89268 -0.238963 4.79307 0.40616 4.18223C1.09407 3.53096 1.99134 2.89784 2.73284 2.28535C3.2172 1.88527 3.70032 1.4856 4.18676 1.08923C4.94861 0.468485 5.6764 -0.481812 6.73859 0.287416L11.5818 4.30514C12.4973 5.45671 11.1435 6.92423 9.89981 6.14015L7.19802 3.93517L7.19802 11.3387C7.19802 11.4204 7.08711 11.7112 7.03975 11.8003C6.91181 12.0416 6.63972 12.2894 6.37843 12.381C5.81473 12.5782 5.09442 12.355 4.82149 11.8011C4.78494 11.7273 4.66406 11.3577 4.66406 11.2975L4.66406 3.93517L4.66406 3.93475Z"
                  fill="white"
                />
              </svg>
            ) : (
              ""
            )}{" "}
            Join Date
          </button>
          <button
            className={`Primary_button`}
            onClick={() => handleSort("transactions")}
          >
            {sortField == "transactions" && sortDirection == "asc" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
              >
                <path
                  d="M4.66406 3.93475L1.96227 6.13974C1.57552 6.38804 1.16012 6.43753 0.73059 6.25275C-0.104787 5.89268 -0.238963 4.79307 0.40616 4.18223C1.09407 3.53096 1.99134 2.89784 2.73284 2.28535C3.2172 1.88527 3.70032 1.4856 4.18676 1.08923C4.94861 0.468485 5.6764 -0.481812 6.73859 0.287416L11.5818 4.30514C12.4973 5.45671 11.1435 6.92423 9.89981 6.14015L7.19802 3.93517L7.19802 11.3387C7.19802 11.4204 7.08711 11.7112 7.03975 11.8003C6.91181 12.0416 6.63972 12.2894 6.37843 12.381C5.81473 12.5782 5.09442 12.355 4.82149 11.8011C4.78494 11.7273 4.66406 11.3577 4.66406 11.2975L4.66406 3.93517L4.66406 3.93475Z"
                  fill="white"
                />
              </svg>
            ) : sortField == "transactions" && sortDirection == "desc" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                className="rotate-180"
              >
                <path
                  d="M4.66406 3.93475L1.96227 6.13974C1.57552 6.38804 1.16012 6.43753 0.73059 6.25275C-0.104787 5.89268 -0.238963 4.79307 0.40616 4.18223C1.09407 3.53096 1.99134 2.89784 2.73284 2.28535C3.2172 1.88527 3.70032 1.4856 4.18676 1.08923C4.94861 0.468485 5.6764 -0.481812 6.73859 0.287416L11.5818 4.30514C12.4973 5.45671 11.1435 6.92423 9.89981 6.14015L7.19802 3.93517L7.19802 11.3387C7.19802 11.4204 7.08711 11.7112 7.03975 11.8003C6.91181 12.0416 6.63972 12.2894 6.37843 12.381C5.81473 12.5782 5.09442 12.355 4.82149 11.8011C4.78494 11.7273 4.66406 11.3577 4.66406 11.2975L4.66406 3.93517L4.66406 3.93475Z"
                  fill="white"
                />
              </svg>
            ) : (
              ""
            )}{" "}
            No. transactions
          </button>
        </div>
      </div>

      <div className="w-[100%] mx-auto Container pshadow mb-20" ref={tableRef}>
        <div
          className={`flex items-center px-8  py-[0.7rem] ${
            isBitramp() ? "bg-[#464646]" : "bg-[#F9F9F9]"
          }  rounded-t-[0.625rem] justify-between font-bold Text dark-blue`}
        >
          <div>Name</div>
          <div>Join date</div>
          <div>No. transactions</div>
          <div></div>
        </div>
        <div className="py-2">
          {sortData(filteredClients || []).map((client) => (
            <div
              ref={(el) => (rowRefs.current[client.id] = el)}
              key={client.id}
            >
              <div
                className="flex items-start px-8 py-1 cursor-pointer"
                onClick={(e) => toggleRow(client.id, e)}
              >
                <div
                  className={`${
                    isBitramp() ? "!bg-[#464646]" : "border"
                  }  p-2 pl-8 Container w-[100%] flex flex-wrap justify-between Text dark-blue ${
                    expandedRow === client.id ? "bg-[#F9F9F9]" : ""
                  }`}
                >
                  <span
                    className={`w-1/6 dark-blue semi-bold  ${
                      expandedRow === client.id ? "mb-4" : ""
                    }`}
                  >
                    {client.is_staff ? (
                      <div className="text-[#1c2492] bold">Admin account</div>
                    ) : (
                      <div>
                        {client.first_name} {client.last_name}
                      </div>
                    )}
                  </span>
                  <span className={`w-3/12`}>
                    Last time Active:{" "}
                    <span className={`${isBitramp() ? "!text-white" : "dark-gray "}`}>
                      {formatDate(client?.created_at ? client.created_at : "")}
                    </span>
                  </span>
                  <span className="w-2/12 ">
                    {client.transaction_count} transactions
                  </span>
                  <button
                    className={`w-1/12 flex justify-end items-center mr-4 text-gray-600  ${
                      expandedRow === client.id ? "mb-3" : ""
                    }`}
                  >
                    {expandedRow === client.id ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M0.153898 13.0533L6.90421 6.303L13.6498 13.0486L13.6509 6.75127L6.89949 -8.23736e-05L0.15782 6.74159L0.153898 13.0533Z"
                          fill={isBitramp() ? "#EE33D9" : "#202A43"}
                          fill-opacity="0.3"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M13.6449 0.745517L6.89462 7.49583L0.149023 0.750229L0.147976 7.04755L6.89933 13.7989L13.641 7.05723L13.6449 0.745517Z"
                          fill={isBitramp() ? "#EE33D9" : "#202A43"}
                        />
                      </svg>
                    )}
                  </button>
                  {expandedRow === client.id && (
                    <div className="custom-table-short w-full h-[45rem] overflow-y-auto">
                      <CustomPOSTableShort
                        clientId={client.id}
                        actionCallback={() => {}}
                        title="Transactions"
                        canEditTransactions={canEditTransactions ?? false}
                        canEditFee={canEditFee ?? false}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default POSClients;
